import { useMemo } from 'react'
import { useDispatch, useSelector } from '../../../../store/hooks'
import {
  BoxV2,
  ButtonV2,
  LoadingLogo,
  NoteV2,
  TableComponentV2,
} from '@labrav/react-components'
import { useStyles } from './FlyOutMigrationProductsList.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck, faPenToSquare } from '@fortawesome/pro-regular-svg-icons'
import { CircularProgress, Typography } from '@mui/material'
import { FormikProps, useFormikContext } from 'formik'
import { FlyoutMicrosoftProductSections } from '../../../../types/addEditProduct'
import cloudStorageLogo from '../../../../common/images/cloud_storage.svg'
import {
  MigrationProduct,
  MigrationProductsData,
} from '../../../../modules/addEditProduct/productsInfo/reducer'
import { getFlyOutMigrationProductsData } from '../../../../modules/addEditProduct/productsInfo/action'
import { ResponsiveColumn } from '../../../../common/components/ResponsiveColumn/ResponsiveColumn'
import { useScreenSize } from '@labrav/common'
import { OfferHeaderIcon } from '../../../OfferHeaderIcon/OfferHeaderIcon'
import {
  MicrosoftHelpAndFAQs,
  MicrosoftHelpAndFAQsItem,
} from '../MicrosoftHelpAndFAQs/MicrosoftHelpAndFAQs'
import {
  startLoading,
  stopLoading,
  LoadingTypes,
  isLoading,
  useAccountId,
} from '@labrav/common'

export const FlyOutMigrationProductsList = ({
  enableUpdateMigrateList,
  setEnableUpdateMigrateList,
  isImpartner,
}: {
  enableUpdateMigrateList: boolean
  setEnableUpdateMigrateList: React.Dispatch<React.SetStateAction<boolean>>
  isImpartner?: boolean
}) => {
  const dispatch = useDispatch()
  const accountId = useAccountId({ product: 'flyout' })
  const { classes } = useStyles({ isImpartner })
  const loading = useSelector<boolean>((state: any) =>
    isLoading(state.loading)(LoadingTypes.FLYOUT_PRODUCT_MIGRATION_PRODUCTS)
  )
  const { isLoading: productLoading } = useSelector<{
    isLoading: boolean
  }>(state => ({
    isLoading: isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS),
  }))
  const { screenWidth } = useScreenSize()
  const columnMaxWidth = useMemo(
    () => `${(screenWidth * 15) / 100}px`,
    [screenWidth]
  )
  const products = useSelector<MigrationProductsData>(
    (state: any) => state.flyOutMigrationProductsInfo.migrationProducts.products
  )

  const productsMapping = {
    'Product ID': 'productId',
    Title: 'alias',
    Type: 'type',
  }

  const { values, errors, setFieldValue, touched } =
    useFormikContext() as FormikProps<FlyoutMicrosoftProductSections>

  const migrationDetails = values.migrationDetails
  const migrationDetailsTouched = touched?.migrationDetails
  const migrationDetailsError = errors?.migrationDetails

  const selectedProductItem = migrationDetails.products

  const fetchMoreData = async () => {
    dispatch(startLoading(LoadingTypes.FLYOUT_PRODUCT_MIGRATION_PRODUCTS))
    try {
      if (accountId) {
        await dispatch(getFlyOutMigrationProductsData(accountId, 'AZURE'))
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      dispatch(stopLoading(LoadingTypes.FLYOUT_PRODUCT_MIGRATION_PRODUCTS))
    }
  }

  const updateSelection = async () => {
    await fetchMoreData()
    await setEnableUpdateMigrateList(true)
  }
  const productsMappingDataObjects = (products?.rows || [])
    .map((productRow: MigrationProduct) => {
      return {
        ...productRow,
        productId: (
          <ResponsiveColumn
            linkText={productRow?.id || ''}
            maxWidth={columnMaxWidth}
          />
        ),
        alias: (
          <ResponsiveColumn
            linkText={productRow?.alias || '-'}
            maxWidth={columnMaxWidth}
          />
        ),
        type: getProductType(productRow?.type || ''),
        isDisableSelection: !enableUpdateMigrateList,
      }
    })
    .filter(item =>
      !enableUpdateMigrateList && item?.id
        ? selectedProductItem?.includes(item?.id)
        : true
    )

  const handleChange = (selected: string[]) => {
    setFieldValue('migrationDetails.products', selected)
  }

  const isError =
    !!migrationDetailsTouched?.products && migrationDetailsError?.products

  if (productLoading) {
    return isImpartner ? (
      <div className={classes.loadingContainer}>
        <CircularProgress size={30} />
      </div>
    ) : (
      <LoadingLogo />
    )
  }

  return (
    <BoxV2
      heading={'Select products to migrate'}
      headingIcon={
        <FontAwesomeIcon
          icon={faListCheck}
          style={{ width: '20px', height: '16px' }}
        />
      }
      secondaryActions={
        <MicrosoftHelpAndFAQs
          item={MicrosoftHelpAndFAQsItem.MIGRATE_PRODUCT}
          isImpartner={isImpartner}
        />
      }
    >
      <div
        className={classes.flexRight}
        data-testid={'migration-products-list'}
      >
        <NoteV2 description={'Only public listings will be migrated.'} />
      </div>
      {loading ? (
        <div className={classes.loaderRootContainer}>
          <img src={cloudStorageLogo} className={classes.LogoImg} />
          <Typography
            data-testid="loading-header"
            className={classes.loaderHeader}
          >
            Loading your products for migration. Please wait...
          </Typography>
          <Typography
            data-testid="loading-sub-header"
            className={classes.loaderSubHeader}
          >
            Hold on. It may take 3 to 5 minutes.
          </Typography>
          <CircularProgress
            data-testid="circular-progress"
            className={classes.loadingSpinner}
          />
        </div>
      ) : (
        <TableComponentV2
          keyName={'migrationDetails.products'}
          objects={productsMappingDataObjects}
          mapping={productsMapping}
          count={
            enableUpdateMigrateList
              ? products?.rows?.length || 0
              : selectedProductItem?.length
          }
          fetchMoreData={() => void 0}
          selectedItem={selectedProductItem}
          onChangeCheckbox={handleChange}
          isHeadCheckboxDisable={!enableUpdateMigrateList}
          tableHeight={
            isError || (!enableUpdateMigrateList && !loading)
              ? 'calc(100vh - 452px)'
              : 'calc(100vh - 396px)'
          }
        />
      )}
      {!enableUpdateMigrateList && !loading && (
        <ButtonV2 styleType="link" onClick={updateSelection}>
          <div className={classes.linkItem}>
            <OfferHeaderIcon
              icon={faPenToSquare}
              style={{
                width: '16px',
                height: '16px',
                padding: '4px',
              }}
            />
            <div>Update selection</div>
          </div>
        </ButtonV2>
      )}
      {isError && (
        <div className={classes.errorMessage}>
          {migrationDetailsError?.products}
        </div>
      )}
    </BoxV2>
  )
}

export const ProductNameWithLink = ({
  name,
  link,
  maxWidth,
}: {
  name?: string
  link: string
  maxWidth?: string
}) => {
  const { classes } = useStyles({ isImpartner: false })
  return (
    <div className={classes.productName}>
      <ResponsiveColumn
        linkText={name || '-'}
        linkUrl={link}
        maxWidth={maxWidth}
      />
    </div>
  )
}

const getProductType = (type: string) => {
  if (type === 'softwareAsAService') {
    return 'SaaS'
  }
  return type
}
